/**
 * Klinger Volume Oscillator (KVO)
 *
 * Volume-based oscillator using trend direction weighted by volume.
 * KVO = EMA(VF, fast) − EMA(VF, slow)
 *
 * Category: Volume | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const klingerIndicator: IndicatorDefinition = {
  id: "klinger",
  name: "Klinger Volume Oscillator",
  shortName: "KVO",
  category: "volume",
  pane: "separate",
  params: [
    { key: "fast", label: "Fast Period", type: "number", default: 34, min: 2, max: 200 },
    { key: "slow", label: "Slow Period", type: "number", default: 55, min: 5, max: 300 },
    { key: "signal", label: "Signal", type: "number", default: 13, min: 2, max: 100 },
  ],
  plots: [
    { key: "kvo", label: "KVO", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "kvo" },
    { key: "signal", label: "Signal", color: "#FF5722", style: "line", lineWidth: 1, priceScaleId: "kvo" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { fast, slow, signal } = params;

    if (candles.length < 2) return [{ key: "kvo", data: [] }, { key: "signal", data: [] }];

    // Volume Force = Volume × |2(dm/cm) − 1| × trend × 100
    const hlc = candles.map(c => c.high + c.low + c.close);
    const vf: number[] = [0];

    for (let i = 1; i < candles.length; i++) {
      const trend = hlc[i] > hlc[i - 1] ? 1 : -1;
      const dm = candles[i].high - candles[i].low;
      const cm = i > 0 && hlc[i] > hlc[i - 1] === (hlc[i - 1] > (i > 1 ? hlc[i - 2] : 0))
        ? (vf.length > 0 ? dm + Math.abs(dm) : dm)
        : dm;
      const vol = candles[i].volume || 0;
      vf.push(vol * Math.abs(2 * dm / (cm || 1) - 1) * trend);
    }

    const emaFast = computeEMA(vf, fast);
    const emaSlow = computeEMA(vf, slow);

    const kvoValues: number[] = [];
    for (let i = 0; i < candles.length; i++) {
      if (!isNaN(emaFast[i]) && !isNaN(emaSlow[i])) {
        kvoValues.push(emaFast[i] - emaSlow[i]);
      } else {
        kvoValues.push(NaN);
      }
    }

    const kvoValid = kvoValues.filter(v => !isNaN(v));
    const sigEma = computeEMA(kvoValid, signal);

    const kvoData: { time: number; value: number }[] = [];
    const sigData: { time: number; value: number }[] = [];
    let validIdx = 0;

    for (let i = 0; i < kvoValues.length; i++) {
      if (!isNaN(kvoValues[i])) {
        kvoData.push({ time: candles[i].time, value: kvoValues[i] });
        if (validIdx < sigEma.length && !isNaN(sigEma[validIdx])) {
          sigData.push({ time: candles[i].time, value: sigEma[validIdx] });
        }
        validIdx++;
      }
    }

    return [{ key: "kvo", data: kvoData }, { key: "signal", data: sigData }];
  },
};
